/**
 * Ticket de venta — construcción PURA del recibo a partir de la orden.
 *
 * Dos salidas del MISMO documento:
 * - `renderReceiptText()` → texto plano monoespaciado (preview en pantalla,
 *   copy/paste, tests legibles);
 * - `encodeReceiptEscPos()` → payload ESC/POS listo para una térmica
 *   (ver `escpos.ts`; el envío al dispositivo es fase manual).
 *
 * Todo el layout es determinista: ancho fijo en columnas (42 = 80 mm,
 * 32 = 58 mm), dinero en unidades menores formateado con `formatMoney`.
 */

import { formatMoney } from "../utils/format";
import { EscPosBuilder } from "./escpos";

/** Línea vendible del ticket (ya filtrada: sin líneas anuladas). */
export interface ReceiptItem {
  name: string;
  qty: number;
  /** Precio unitario EFECTIVO en centavos (modificadores incluidos). */
  unitPrice: number;
  /** Total de línea en centavos. */
  lineTotal: number;
}

export interface ReceiptPayment {
  method: string;
  amount: number;
  tip?: number;
  tendered?: number;
  changeGiven?: number;
}

export interface ReceiptData {
  restaurantName: string;
  branchName?: string | null;
  /** Id de la orden (se imprime acortado a 8 chars). */
  orderId: string;
  /** ISO timestamp de cierre/cobro (o apertura si sigue abierta). */
  timestamp?: string | null;
  tableName?: string | null;
  employeeName?: string | null;
  items: ReceiptItem[];
  subtotal: number;
  discount: number;
  taxTotal: number;
  total: number;
  payments: ReceiptPayment[];
  /** Mensaje de pie ("¡Gracias por su visita!") — viene traducido del caller. */
  footer?: string | null;
  /** Etiquetas traducidas (el módulo es puro: no importa i18n). */
  labels?: Partial<ReceiptLabels>;
}

export interface ReceiptLabels {
  order: string;
  table: string;
  server: string;
  subtotal: string;
  discount: string;
  tax: string;
  total: string;
  tendered: string;
  change: string;
  tip: string;
}

const DEFAULT_LABELS: ReceiptLabels = {
  order: "Order",
  table: "Table",
  server: "Server",
  subtotal: "Subtotal",
  discount: "Discount",
  tax: "Tax",
  total: "TOTAL",
  tendered: "Tendered",
  change: "Change",
  tip: "Tip",
};

/** Estilo de línea, para que el encoder ESC/POS aplique énfasis/tamaño. */
export type ReceiptLineStyle = "title" | "bold" | "normal";

export interface ReceiptLine {
  text: string;
  style: ReceiptLineStyle;
  align: "left" | "center";
}

/** Ancho en columnas: 42 ≈ 80 mm, 32 ≈ 58 mm. */
export const RECEIPT_WIDTH_80MM = 42;
export const RECEIPT_WIDTH_58MM = 32;

/** Reparte `left` y `right` en una línea de `width` cols (right alineado). */
export function twoColumns(left: string, right: string, width: number): string {
  const space = width - right.length;
  if (space <= 0) {
    return right.slice(0, width);
  }
  const l = left.length > space - 1 ? `${left.slice(0, Math.max(0, space - 2))}…` : left;
  return l + " ".repeat(Math.max(1, space - l.length)) + right;
}

/** Trocea un texto en líneas de ≤ width (corte duro, sin huérfanos). */
export function wrapText(text: string, width: number): string[] {
  const out: string[] = [];
  let rest = text;
  while (rest.length > width) {
    out.push(rest.slice(0, width));
    rest = rest.slice(width);
  }
  out.push(rest);
  return out;
}

function divider(width: number): string {
  return "-".repeat(width);
}

/** id corto imprimible (8 chars del UUID). */
export function shortOrderId(orderId: string): string {
  return orderId.replaceAll("-", "").slice(0, 8).toUpperCase();
}

/**
 * Construye el documento del ticket: lista ordenada de líneas con estilo.
 * Determinista — mismos datos, mismas líneas.
 */
export function buildReceiptLines(data: ReceiptData, width = RECEIPT_WIDTH_80MM): ReceiptLine[] {
  const labels: ReceiptLabels = { ...DEFAULT_LABELS, ...data.labels };
  const lines: ReceiptLine[] = [];
  const center = (text: string, style: ReceiptLineStyle = "normal") => {
    lines.push({ text, style, align: "center" });
  };
  const left = (text: string, style: ReceiptLineStyle = "normal") => {
    lines.push({ text, style, align: "left" });
  };

  center(data.restaurantName, "title");
  if (data.branchName) {
    center(data.branchName);
  }
  center("");

  left(`${labels.order} #${shortOrderId(data.orderId)}`, "bold");
  if (data.timestamp) {
    // ISO → "YYYY-MM-DD HH:mm" (sin depender de la TZ del runtime).
    left(data.timestamp.slice(0, 16).replace("T", " "));
  }
  if (data.tableName) {
    left(`${labels.table}: ${data.tableName}`);
  }
  if (data.employeeName) {
    left(`${labels.server}: ${data.employeeName}`);
  }
  left(divider(width));

  for (const item of data.items) {
    const price = formatMoney(item.lineTotal);
    const label = `${item.qty}x ${item.name}`;
    left(twoColumns(label, price, width));
    if (item.qty > 1) {
      left(`   @ ${formatMoney(item.unitPrice)}`);
    }
  }
  left(divider(width));

  left(twoColumns(labels.subtotal, formatMoney(data.subtotal), width));
  if (data.discount > 0) {
    left(twoColumns(labels.discount, `-${formatMoney(data.discount)}`, width));
  }
  if (data.taxTotal > 0) {
    left(twoColumns(labels.tax, formatMoney(data.taxTotal), width));
  }
  left(twoColumns(labels.total, formatMoney(data.total), width), "title");

  if (data.payments.length > 0) {
    left(divider(width));
    for (const p of data.payments) {
      left(twoColumns(p.method, formatMoney(p.amount), width));
      if (p.tip && p.tip > 0) {
        left(twoColumns(`  ${labels.tip}`, formatMoney(p.tip), width));
      }
      if (p.tendered !== undefined && p.tendered > p.amount) {
        left(twoColumns(`  ${labels.tendered}`, formatMoney(p.tendered), width));
      }
      if (p.changeGiven && p.changeGiven > 0) {
        left(twoColumns(`  ${labels.change}`, formatMoney(p.changeGiven), width));
      }
    }
  }

  if (data.footer) {
    center("");
    for (const l of wrapText(data.footer, width)) {
      center(l);
    }
  }

  return lines;
}

/** Render de preview: texto plano monoespaciado (una línea por entrada). */
export function renderReceiptText(lines: ReceiptLine[], width = RECEIPT_WIDTH_80MM): string {
  return lines
    .map((l) => {
      if (l.align === "center" && l.text.length > 0 && l.text.length < width) {
        const pad = Math.floor((width - l.text.length) / 2);
        return " ".repeat(pad) + l.text;
      }
      return l.text;
    })
    .join("\n");
}

/** Payload ESC/POS del ticket (init + líneas con estilo + feed + corte). */
export function encodeReceiptEscPos(
  lines: ReceiptLine[],
  opts?: { openDrawer?: boolean },
): Uint8Array {
  const b = new EscPosBuilder().init();
  let align: "left" | "center" = "left";
  let bold = false;
  let double = false;
  for (const l of lines) {
    if (l.align !== align) {
      b.align(l.align);
      ({ align } = l);
    }
    const wantBold = l.style === "bold" || l.style === "title";
    if (wantBold !== bold) {
      b.bold(wantBold);
      bold = wantBold;
    }
    const wantDouble = l.style === "title";
    if (wantDouble !== double) {
      b.doubleSize(wantDouble);
      double = wantDouble;
    }
    b.line(l.text);
  }
  if (bold) {
    b.bold(false);
  }
  if (double) {
    b.doubleSize(false);
  }
  b.feed(4);
  if (opts?.openDrawer) {
    b.openDrawer();
  }
  b.cut();
  return b.payload();
}
